import type { Component } from "solid-js";
import { createEffect, createMemo, createSignal, on, Show, untrack } from "solid-js";
import { isEmpty, isEqual, isNil, omit } from "lodash-es";
import { SingleSelect } from "./Select";
import { softContains } from "../../utils/stringUtil";
import { Popover, type PopoverTriggerProps } from "@ark-ui/solid";
import {
  findCategoryByValue,
  getAvailableCategories,
  getFilteredOptions,
  getRenderedHTMLOptions,
} from "../../utils/menu/searchableMenuUtil";
import SelectMenuWithSearchOptions from "./SelectMenuWithSearchOption";
import SearchBar from "../searchBar/SearchBar";
import { useTranslation } from "../../state/i18n";
import MenuEmptyState from "./MenuEmptyState";
import Portal from "../../utils/Portal";
import type { FlattenedSelectOption, SelectOptions, SelectOption } from "../../types/Menu";

type SelectMenuWithSearchProps<T> = {
  id: string;
  ariaLabel: string;
  options: SelectOptions<T>;
  flattenedOptions: FlattenedSelectOption<T>[];
  selectedOptions: SelectOption<T>[];
  onSelect: (value: SelectOption<T>) => void;
  onClose?: () => void;
  width: "matchTrigger" | "240px" | "480px";
  placeholder?: string;
  errorMessage?: string;
  multiple: boolean;
  open?: boolean;
  Trigger: Component<Omit<PopoverTriggerProps, "children">>;
};

const SelectMenuWithSearch = <T,>(props: SelectMenuWithSearchProps<T>) => {
  const { t } = useTranslation();
  const listboxId = untrack(() => `${props.id}-listbox`);
  const [query, setQuery] = createSignal("");
  const [openPopover, setOpenPopover] = createSignal(untrack(() => props.open) ?? false);

  const categories = createMemo(() => getAvailableCategories(props.options));
  const [currentCategory, setCurrentCategory] = createSignal(
    untrack(() => (isEmpty(categories()) ? null : categories()[0].value)),
  );

  createEffect(
    on(categories, (categories) => {
      // Options can load after mount (e.g. from a query), so pick the first category once one becomes available.
      if (isNil(currentCategory()) && !isEmpty(categories)) {
        setCurrentCategory(categories[0].value);
      }
    }),
  );

  const groupedVisibleOptions = createMemo(() => {
    if (isNil(currentCategory())) {
      return null;
    }
    if (isEmpty(query())) {
      return props.options[currentCategory()!];
    }

    return getFilteredOptions(props.options[currentCategory()!], query());
  });

  const flattenedVisibleOptions = createMemo(() => {
    if (isEmpty(props.flattenedOptions)) {
      return [];
    }
    const optionsForCurrentCategory = props.flattenedOptions.filter((option) => option.title === currentCategory());
    if (!query()) {
      return optionsForCurrentCategory;
    }

    return optionsForCurrentCategory.filter((option) => {
      if ("pill" in option && !isNil(option.pill)) {
        return softContains(option.label, query()) || softContains(option.pill as string, query());
      }
      return softContains(option.label, query());
    });
  });

  const [highlightedOption, setHighlightedOption] = createSignal<SelectOption<T> | null>(null);
  const highlightedIndex = createMemo(() => {
    if (isEmpty(flattenedVisibleOptions())) {
      return -1;
    }
    return flattenedVisibleOptions().findIndex((option) => option.value === highlightedOption()?.value);
  });

  const moveHighlight = (offset: number) => {
    const nextIndex = highlightedIndex() + offset;
    const nextOption = flattenedVisibleOptions()[nextIndex];

    if (!isNil(nextOption)) {
      setHighlightedOption(nextOption);
      const optionToScrollTo = getRenderedHTMLOptions(listboxId)[nextIndex];
      if (!isNil(optionToScrollTo)) {
        optionToScrollTo.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveHighlight(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveHighlight(-1);
        break;
      case "Enter":
        if (highlightedIndex() >= 0 && !isNil(highlightedOption())) {
          e.preventDefault();
          props.onSelect(highlightedOption()!);
          if (!props.multiple) {
            handleClosePopover();
          }
        }
        break;
    }
  };

  const handleQueryChange = (value: string): void => {
    setQuery(value);
    setHighlightedOption(null);

    requestAnimationFrame(() => {
      if (!isEmpty(flattenedVisibleOptions())) {
        setHighlightedOption(flattenedVisibleOptions()[0]);
      }
    });
  };

  const handleCategoryChange = (value?: string): void => {
    setCurrentCategory(value ?? categories()[0].value);
    setHighlightedOption(null);
  };

  const handleOpenPopover = () => {
    setOpenPopover(true);
    if (isEmpty(props.selectedOptions)) {
      return;
    }

    const lastSelectedOption = props.selectedOptions[props.selectedOptions.length - 1];
    const categoryKey = findCategoryByValue(props.options, lastSelectedOption.value);
    if (!isNil(categoryKey)) {
      setCurrentCategory(categoryKey);
    }

    requestAnimationFrame(() => {
      if (!isNil(lastSelectedOption)) {
        setHighlightedOption(lastSelectedOption);

        const indexOfLastSelectedOption = flattenedVisibleOptions().findIndex((option) =>
          isEqual(option.value, lastSelectedOption.value),
        );
        const optionToScrollTo = getRenderedHTMLOptions(listboxId)[indexOfLastSelectedOption];
        if (!isNil(optionToScrollTo)) {
          optionToScrollTo.scrollIntoView({ behavior: "instant", block: "nearest" });
        }
      }
    });
  };

  const handleClosePopover = () => {
    setOpenPopover(false);
    setHighlightedOption(null);
    setQuery("");
    if (!isEmpty(categories())) {
      setCurrentCategory(categories()[0].value);
    }
    props.onClose?.();
  };

  const showCategorySelect = createMemo(() => !isEmpty(categories()) && categories().length > 1);

  return (
    <Popover.Root
      open={openPopover()}
      onOpenChange={(state) => {
        state.open ? handleOpenPopover() : handleClosePopover();
      }}
      positioning={{ placement: "bottom-start", gutter: 4, sameWidth: props.width === "matchTrigger" }}
    >
      <Popover.Trigger asChild={(triggerProps) => <props.Trigger {...omit(triggerProps(), "children")} />} />
      <Show when={openPopover()}>
        <Portal>
          <Popover.Positioner>
            <Popover.Content
              aria-label={props.ariaLabel}
              class="relative z-50 animate-popover-open overflow-hidden rounded-sm bg-surface-default pt-1 shadow-elevation-light-3"
              classList={{
                "w-60": props.width === "240px",
                "w-120": props.width === "480px",
              }}
            >
              <div class="grid grid-cols-6 gap-x-2 px-3 py-2">
                <div classList={{ "col-span-4": showCategorySelect(), "col-span-full": !showCategorySelect() }}>
                  <SearchBar
                    aria-controls={listboxId}
                    aria-expanded="true"
                    role="combobox"
                    placeholder={props.placeholder ?? t(($) => $.MENU_SEARCH_PLACEHOLDER_DEFAULT)}
                    onFocusOut={() => setHighlightedOption(null)}
                    onKeyDown={handleKeyDown}
                    query={query()}
                    onQueryChange={handleQueryChange}
                  />
                </div>
                <div class="col-span-2">
                  <Show when={showCategorySelect()}>
                    <SingleSelect<string>
                      ariaLabel={t(($) => $.MENU_CATEGORY_SELECT_ARIA_LABEL)}
                      options={categories()}
                      value={currentCategory()!}
                      onSelect={(value) => handleCategoryChange(value)}
                      sameWidth={false}
                      hasError={false}
                    />
                  </Show>
                </div>
              </div>
              <div
                // Scroll padding matches sticky group header to prevent option overlap.
                class="h-72 scroll-pt-7 overflow-y-auto overscroll-y-none rounded-sm px-1 pb-1 -outline-offset-1 focus-visible:outline-default"
              >
                <div
                  id={listboxId}
                  role="listbox"
                  aria-label={[
                    !isNil(currentCategory())
                      ? t(($) => $.MENU_LISTBOX_ARIA_LABEL_WITH_CATEGORY, { category: currentCategory() })
                      : t(($) => $.MENU_LISTBOX_ARIA_LABEL),
                    query() && t(($) => $.MENU_LISTBOX_ARIA_LABEL_FILTERED_BY, { query: query() }),
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  tabIndex={-1}
                  class="h-full"
                >
                  <Show
                    when={!isEmpty(groupedVisibleOptions()) && !isEmpty(currentCategory())}
                    fallback={
                      <MenuEmptyState
                        text={
                          query() ? t(($) => $.SELECT_SEARCH_EMPTY_STATE_TITLE) : t(($) => $.SELECT_EMPTY_STATE_TITLE)
                        }
                      />
                    }
                  >
                    <SelectMenuWithSearchOptions
                      options={groupedVisibleOptions()!}
                      category={currentCategory()!}
                      selectedOptions={props.selectedOptions}
                      highlightedOption={highlightedOption()}
                      handleOptionSelect={(option) => {
                        props.onSelect(option);
                        if (!props.multiple) {
                          handleClosePopover();
                        }
                      }}
                    />
                  </Show>
                </div>
              </div>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Show>
    </Popover.Root>
  );
};

export default SelectMenuWithSearch;
